import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Check } from 'lucide-react';
import { AuthLayout } from './LoginPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/* Public page: a teammate lands here from the invite email and sets a password. */
export default function AcceptInvitePage({ onLogin }) {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const submitting = useRef(false);
  const [error, setError] = useState(token ? '' : 'This invite link is missing its token. Ask your workspace admin for a new invitation.');

  const handleSubmit = async e => {
    e.preventDefault();
    if (submitting.current) return;
    if (!token) { setError('This invite link is missing its token. Ask your workspace admin for a new invitation.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setError("Passwords don't match"); return; }
    submitting.current = true;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, name: name.trim() || undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Invite invalid or expired');
      await onLogin(data);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message || 'Could not join the workspace. Try again.');
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <h2>You're invited.</h2>
      <p>Set a password to join your team's workspace.</p>
      {error && <div id="invite-error" className="sp-auth-notice" data-tone="error" role="alert">{error}</div>}
      <form onSubmit={handleSubmit} aria-busy={loading} aria-describedby={error ? 'invite-error' : undefined}>
        <div className="sp-auth-field">
          <label htmlFor="invite-name">Your name <span>(optional)</span></label>
          <input id="invite-name" name="name" value={name} onChange={e => setName(e.target.value)} autoComplete="name" disabled={loading || !token} data-testid="invite-name" />
        </div>
        <div className="sp-auth-field">
          <label htmlFor="invite-password">Password</label>
          <input id="invite-password" name="password" type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" aria-describedby="invite-password-hint" required minLength={8} disabled={loading || !token} data-testid="invite-password" />
          <small id="invite-password-hint">Use at least 8 characters.</small>
        </div>
        <div className="sp-auth-field">
          <label htmlFor="invite-confirm">Confirm password</label>
          <input id="invite-confirm" name="confirm" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="new-password" required minLength={8} disabled={loading || !token} data-testid="invite-confirm" />
        </div>
        <button type="submit" disabled={loading || !token} className="sp-primary-button sp-login-submit" data-testid="invite-submit">
          {loading ? <Loader2 size={15} className="sp-spin" aria-hidden="true" /> : <Check size={15} aria-hidden="true" />}
          {loading ? 'Joining workspace…' : 'Join workspace'}
        </button>
      </form>
      <div className="sp-auth-actions">{loading
        ? <span className="sp-auth-link" aria-disabled="true">Back to sign in</span>
        : <a className="sp-auth-link" href="/"><ArrowLeft size={15} aria-hidden="true" /> Back to sign in</a>}
      </div>
    </AuthLayout>
  );
}
